<template>
  <div
    :class="{
      'postcode-search--homepage': isHomepage,
      'postcode-search--fixed': fixed,
      'postcode-search--error': hasError,
      'postcode-search--success': isSuccess
    }"
    class="postcode-search"
  >
    <form @submit.prevent="submit">
      <div :class="{container}" class="postcode-search__field">
        <LocationPinSVG
          v-if="!isSuccess && useLocationPinIcon"
          :stroke="locationPinIconColor"
          class="postcode-search__location-icon"
          alt="location pin icon"
        />
        <img v-else-if="isSuccess" :src="require(`../assets/icons/${postcodeSuccessIcon}`)" alt="check circle icon">
        <div v-if="!isSuccess" class="postcode-search__content-wrap" :class="{ 'extra-padding': useLocationPinIcon }">
          <input
            v-if="!isSuccess"
            ref="input"
            :value="value"
            class="postcode-search__input"
            type="text"
            :maxlength="maxlength"
            :placeholder="computedPlaceholder"
            aria-label="Postcode input"
            @input="input"
          >
        </div>
        <div v-else class="postcode-search__code-box">
          <span class="postcode-search__postcode" :class="[colorTheme ? `postcode-search__postcode--${colorTheme}` : '']">{{ value }}</span>
          <span class="postcode-search__inline-edit" @click="edit">Tap to Edit</span>
        </div>
        <MicButton
          v-if="isReady"
          class="postcode-search__button"
          :shadow="false"
          :compact="true"
          :theme="colorTheme"
          type="submit"
          :has-arrow="!icon"
          :hide-arrow-mobile="true"
        >
          <span class="postcode-search__button-text">{{ buttonLabel }}</span>
          <img
            v-if="icon"
            :src="require(`../assets/icons/${icon}`)"
            alt="button icon"
            class="postcode-search__button-icon"
          >
          <img
            v-else-if="showSearchIcon || isTablet"
            src="../assets/icons/magnifying-glass.svg"
            alt="magnifying glass icon"
            class="postcode-search__button-icon"
          >
          <img
            v-if="iconHover"
            :src="require(`../assets/icons/${iconHover}`)"
            alt="button icon"
            class="postcode-search__button-icon postcode-search__button-icon--white"
          >
          <img
            v-else-if="showSearchIcon || isTablet"
            src="../assets/icons/magnifying-glass-white.svg"
            alt="magnifying glass icon"
            class="postcode-search__button-icon postcode-search__button-icon--white"
          >
        </MicButton>
        <SpinningLoader v-if="isLoading" />
        <MicButton
          v-if="isSuccess"
          :theme="postCodeBtnTheme"
          class="postcode-search__edit"
          type="button"
          :compact="true"
          @click.prevent="edit"
        >
          Edit
        </MicButton>
      </div>
    </form>
    <div v-if="hasError" class="postcode-search__error">
      <div class="postcode-search__box">
        <div class="postcode-search__triangle" />
        <p class="postcode-search__error-text">
          <img
            class="postcode-search__error-icon"
            src="../assets/icons/error-sign.svg"
            alt="error sign"
          >
          {{ errorMessage }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import SpinningLoader from '../icons/SpinningLoader'
import MicButton from '../buttons/MicButton'
import LocationPinSVG from '../icons/LocationPinSVG'

const STATUSES = Object.freeze(['success', 'ready', 'loading', 'error'])

export default {
  name: 'PostcodeInput',

  components: {
    SpinningLoader,
    MicButton,
    LocationPinSVG
  },

  props: {
    fixed: {
      type: Boolean,
      default: false
    },
    container: {
      type: Boolean,
      default: false
    },
    isHomepage: {
      type: Boolean,
      default: false
    },
    hasError: {
      type: Boolean,
      default: false
    },
    value: {
      type: String,
      required: true
    },
    focusKey: {
      type: Boolean,
      default: false
    },
    buttonLabel: {
      type: String,
      default: 'Start quote'
    },
    showSearchIcon: {
      type: Boolean,
      default: false
    },
    status: {
      type: String,
      required: true,
      validator (value) {
        return STATUSES.includes(value)
      }
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    isTablet: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    errorMessage: {
      type: String,
      default: 'Please enter a valid postcode to continue.'
    },
    maxlength: {
      type: Number,
      default: 10
    },
    icon: {
      type: String,
      default: null
    },
    iconHover: {
      type: String,
      default: null
    },
    useLocationPinIcon: {
      type: Boolean,
      default: true
    },
    locationPinIconColor: {
      type: String,
      default: '#032942'
    },
    postcodeSuccessIcon: {
      type: String,
      default: 'check-circle.svg'
    },
    postCodeBtnTheme: {
      type: String,
      default: 'outline'
    },
    colorTheme: {
      type: String,
      default: ''
    }
  },

  computed: {
    isSuccess () {
      return this.status === 'success'
    },
    isReady () {
      return this.status === 'ready'
    },
    isLoading () {
      return this.status === 'loading'
    },
    computedPlaceholder () {
      return this.placeholder ? this.placeholder : 'Enter a valid UK postcode'
    }
  },

  watch: {
    focusKey () {
      this.$refs.input.focus()
    }
  },

  methods: {
    submit () {
      this.$emit('submit')
    },

    edit () {
      this.$emit('edit')
    },

    input ({ currentTarget }) {
      this.$emit('input', currentTarget.value.trim())
    }
  }
}
</script>

<style scoped lang="scss">
.postcode-search {
  position: relative;
  border-radius: $border-radius-medium;
  box-shadow: $postcode-shadow;
  background-color: $white;

  &--error {
    border: 2px solid $error-red;
  }

  &--success {
    background-color: $postcode-background-success;

    input {
      background-color: $postcode-background-success;
    }
  }

  &--homepage {
    max-width: 650px;
    width: 100%;
  }

  &__field {
    width: 100%;
    min-height: 80px;
    display: flex;
    align-items: center;
    border-radius: $border-radius-medium;
    overflow: hidden;
    padding: $spacing-12 $spacing-12 $spacing-12 $spacing-20;

    @media (max-width: $screen-md) {
      min-height: 56px;
    }
  }

  &__code-box {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }

  &__inline-edit {
    display: none;
  }

  &__content-wrap {
    padding: 0 $spacing-8;
    position: relative;
    flex-grow: 1;
    text-align: left;
  }

  &__input {
    border: 0;
    font-weight: bold;
    outline: none;
    text-align: left;
    width: 100%;
    color: $postcode-edit-text;

    &::placeholder {
      font-weight: normal;
      color: $postcode-placeholder-text;
      opacity: 0.4;
    }
  }

  &__button {
    &:hover {
      .postcode-search__button-icon {
        display: none;

        &--white {
          display: block;
        }
      }
    }
  }

  &__button-text {
    display: flex;
    align-items: center;
  }

  &__button-icon {
    margin-left: $spacing-12;

    &--white {
      display: none;
    }

    @media (max-width: $screen-md) {
      margin-left: 0;
    }
  }

  &__button-caret {
    margin-left: $spacing-8;
  }

  &__postcode {
    border: 0;
    padding: 0 $spacing-20;
    font-weight: bold;
    outline: none;
    text-align: left;
    width: 100%;

    &--purple {
      color: $bionic-blue;
    }
  }

  &__error {
    position: absolute;
    top: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    max-width: 414px;
    margin: $spacing-0 auto;
    z-index: 2;
  }

  &__box {
    position: relative;
    background-color: $white;
    border: 1px solid rgba(3, 41, 66, 0.3);
    border-radius: $border-radius-medium;
    margin: $spacing-0 auto;
    padding: $spacing-24;
    margin-top: $spacing-8;

    @media (max-width: $screen-sm) {
      padding: $spacing-16;
    }
  }

  &__triangle {
    position: absolute;
    width: 0;
    height: 0;
    border-left: 7px solid transparent;
    border-right: 7px solid transparent;
    border-bottom: 7px solid rgba(3, 41, 66, 0.3);
    top: -8px;
    left: 0;
    right: 0;
    margin: $spacing-0 auto;

    &::before {
      content: " ";
      width: 0;
      height: 0;
      border-left: 7px solid transparent;
      border-right: 7px solid transparent;
      border-bottom: 7px solid white;
      top: 1px;
      left: -7px;
      right: 0;
      margin: $spacing-0 auto;
      position: absolute;
    }
  }

  &__error-text {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 16px;
    font-weight: bold;
    color: $text-color;
    margin-bottom: $spacing-0;
  }

  &__error-icon {
    margin-right: $spacing-16;
  }

  &--fixed {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 10;
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;

    .postcode-search {
      &__error {
        top: calc(-100% + (-8px * 2));
      }

      &__triangle {
        transform: rotate(180deg);
        top: 100%;
      }
    }
  }

  .extra-padding {
    padding: 0 $spacing-20;
  }

  @media (max-width: $screen-md) {
    &--success {
      .postcode-search {
        &__field {
          flex-direction: row-reverse;
          padding: $spacing-12 $spacing-24;
        }

        &__edit {
          display: none;
        }

        &__code-box {
          display: flex;
          flex-direction: column;
        }

        &__postcode {
          padding: $spacing-0;
        }

        &__inline-edit {
          display: block;
          font-size: 15px;
          font-weight: 500;
          text-decoration: underline;
          color: $postcode-edit-text;
          text-align: left;
        }
      }
    }

    &__input,
    &__postcode {
      font-size: 16px;
    }

    &__content-wrap {
      padding: 0 $spacing-12;
    }

    &__button.mic-button {
      border-radius: 50%;
      width: 40px;
      min-width: 40px;
      height: 40px;
      padding: $spacing-0;
    }

    &__button-text {
      display: none;
    }

    // &__location-icon {
    //   width: 10px;
    //   height: 14px;
    // }
  }
}
</style>
